import { formatShareBps } from "@loot-os/api-contract";
import {
  type Billing,
  planApprovalCopy,
  spendingLimitCopy,
  usePlanApproval,
  useSpendingLimitRaise,
} from "./plan";

/** A banner's action, in its body rather than its `secondary-actions` slot, which it did not show. */
function BannerAction({
  label,
  mutation,
}: {
  label: string;
  mutation: { isPending: boolean; mutate: () => void };
}) {
  return (
    <s-box paddingBlockStart="small">
      <s-button
        variant="primary"
        loading={mutation.isPending || undefined}
        disabled={mutation.isPending || undefined}
        onClick={() => mutation.mutate()}
      >
        {label}
      </s-button>
    </s-box>
  );
}

/**
 * The Loot Boxes plan, when the merchant has something to do about it: approve it before any box
 * is created or goes on sale, approve again after a decline, a cancellation or an expiry, or
 * approve a new fee. On Home, the setup guide's first step asks for an approval instead
 * (`App.tsx`), so the two never ask at once; the frozen plan and a new fee stay here. Approval
 * happens on Shopify's own page, opened at the top level; Shopify returns the merchant to the
 * app, which then reads the plan back.
 */
function PlanBanner({ billing }: { billing: Billing }) {
  const start = usePlanApproval();
  if (!billing.approvalNeeded && billing.status === "active") return null;

  if (billing.status === "frozen") {
    return (
      <s-banner tone="critical" heading="Your boxes are paused">
        Shopify froze the Loot Boxes plan because the store's Shopify bill is unpaid. Boxes show
        sold out until it is paid.
      </s-banner>
    );
  }
  if (billing.status === "active") {
    return (
      <s-banner
        tone="info"
        heading={`Loot Boxes pricing is now ${formatShareBps(billing.feeBps)} of box sales`}
      >
        You are billed {formatShareBps(billing.approvedRateBps ?? billing.feeBps)} until you approve
        the new rate.
        <BannerAction label="Review new pricing" mutation={start} />
      </s-banner>
    );
  }
  const copy = planApprovalCopy(billing.status, billing.feeBps);
  return (
    <s-banner tone={billing.status === "none" ? "info" : "warning"} heading={copy.heading}>
      {copy.body}
      <BannerAction label={copy.action} mutation={start} />
    </s-banner>
  );
}

/**
 * The plan's spending limit, once Shopify has billed 90% of it this cycle or refused a fee past
 * it: the merchant raises it on Shopify's own page, and the admin reads the plan back on return.
 */
function SpendingLimitBanner({ billing }: { billing: Billing }) {
  const raise = useSpendingLimitRaise();
  const copy = spendingLimitCopy(billing);
  if (!copy) return null;
  return (
    <s-banner tone="warning" heading={copy.heading}>
      {copy.body}
      <BannerAction label={copy.action} mutation={raise} />
    </s-banner>
  );
}

/** Every plan banner the merchant has something to do about, the plan's own first. */
export function BillingBanner({ billing }: { billing: Billing }) {
  return (
    <>
      <PlanBanner billing={billing} />
      <SpendingLimitBanner billing={billing} />
    </>
  );
}
